import type { DirectorTimelineEvent } from '@/types/director'
import { formatDeadline } from '@/utils/caseList'
import {
  CheckCircle2, Undo2, FileUp,
  FileText, BellRing, Clock, GitBranch, AlertTriangle, RefreshCw,
} from 'lucide-react'

interface CaseTimelineBlockProps {
  events: DirectorTimelineEvent[]
  loading: boolean
  error: string | null
  onRetry: () => void
}

const EVENT_ICONS: Record<DirectorTimelineEvent['type'], React.ReactNode> = {
  request_submitted: <FileText className="w-3.5 h-3.5" />,
  gate_triggered: <GitBranch className="w-3.5 h-3.5" />,
  decision: <Clock className="w-3.5 h-3.5" />,
  documents_requested: <FileText className="w-3.5 h-3.5" />,
  document_uploaded: <FileUp className="w-3.5 h-3.5" />,
  reminder_sent: <BellRing className="w-3.5 h-3.5" />,
  gate_released_approved: <CheckCircle2 className="w-3.5 h-3.5" />,
  gate_released_returned: <Undo2 className="w-3.5 h-3.5" />,
}

function eventColor(type: DirectorTimelineEvent['type']): string {
  switch (type) {
    case 'gate_released_approved': return 'bg-green-500'
    case 'gate_released_returned': return 'bg-orange-500'
    case 'reminder_sent': return 'bg-amber-500'
    case 'document_uploaded': return 'bg-teal-500'
    case 'gate_triggered': return 'bg-indigo-500'
    default: return 'bg-gray-400'
  }
}

function formatWhen(iso: string | null): string {
  if (!iso) return 'Zeit unbekannt'
  return formatDeadline(iso) ?? iso
}

/** Fallakte v2 (P5.7): Aktivitäten-Timeline (volles Audit, P3/E4). */
export function CaseTimelineBlock({ events, loading, error, onRetry }: CaseTimelineBlockProps) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <h2 className="text-sm font-bold mb-3">Aktivität</h2>

      {loading && <p className="text-xs text-gray-400">Timeline wird geladen…</p>}

      {error && !loading && (
        <div className="flex items-center gap-2 text-xs text-red-600">
          <AlertTriangle className="w-3.5 h-3.5" />
          <span className="flex-1">{error}</span>
          <button type="button" onClick={onRetry}
            className="flex items-center gap-1 text-blue-600 hover:underline">
            <RefreshCw className="w-3 h-3" /> Erneut
          </button>
        </div>
      )}

      {!loading && !error && events.length === 0 && (
        <p className="text-xs text-gray-400">Keine Aktivitäten aufgezeichnet.</p>
      )}

      {!loading && !error && events.length > 0 && (
        <ol className="space-y-0 text-xs">
          {events.map((e, idx) => (
            <li key={`${e.type}-${e.occurred_at ?? idx}-${idx}`} className="flex gap-2.5">
              <div className="flex flex-col items-center">
                <span
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-white shrink-0 ${eventColor(e.type)}`}
                  data-event-type={e.type}
                >
                  {EVENT_ICONS[e.type]}
                </span>
                {idx < events.length - 1 && <span className="w-px flex-1 bg-gray-200 my-0.5" />}
              </div>
              <div className="pb-3 min-w-0">
                <p className="font-semibold text-gray-800 leading-snug">
                  {e.title}
                  {e.actor_name && <span className="font-normal text-gray-500"> — {e.actor_name}</span>}
                </p>
                <p className="text-[11px] text-gray-400">{formatWhen(e.occurred_at)}</p>
                {e.detail && (
                  <p className="text-[11px] text-gray-600 mt-0.5 break-words">{e.detail}</p>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}
